import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxEssentiaModule } from 'ngx-essentia';
import { NGXLogger as LoggerService } from "ngx-logger";
import { ResponsiveService } from '../../../shared/services/responsive.service';
import { TranslationGeneralService } from '../../../shared/translation/translation.general.service';
import { UIMesageService } from '../../../shared/services/ui-message.service';
import { ClipboardService } from '../../../shared/services/clipboard.service';
import { CurrentPollService } from '../../core/current-poll.service';
import { HeaderService } from '../poll-header/header.service';
import { JoinLinkParts, NavigationService } from '../../core/navigation.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { ProviderSignInComponent } from '../../../user/shared/provider-sign-in/provider-sign-in.component';
import { EmailSignupLoginComponent } from '../../../user/shared/email-signup-login/email-signup-login.component';
import { MatCardModule } from '@angular/material/card';
import { GoogleAnalyticsService } from '../../../shared/services/google-analytics.service';
import { MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '../../../user/auth.service';
import { UserService } from '../../../user/user.service';
import { RouteContextService } from '../../../shared/services/route-context.service';
import { isConnectionLost, isNetworkRequestFailed } from '../../core/poll-errors';

/**
 * The "organizer link" dialog (concept-poll-scoped-access §6 and §0 revision 2026-10-18, workorders 1.4 task 2
 * and 1.9): on a poll page the join link with the strongest key the user holds - the organizer link (owner /
 * co-owner: whoever has it is a co-organizer, for the user himself or to pass on) or the link to his own
 * answer (voter) - copied to the clipboard right away; without a poll route (decided by
 * `RouteContextService.pollRoute`, not by the lingering poll context - workorder 1.7), or when the user
 * holds nothing on the poll, the hint to create an account or log in (phase 1; the bundle link is
 * phase 2). Since 1.9 this is the one dialog for organizers: there is no separate editor link. Since 1.10 every
 * organizer reaches it, signed in or anonymous: for a signed-in user it is a pass-on dialog (headline "pass this
 * link on to co-organizers", no sign-up offer - they come back by logging in); the anonymous user keeps the
 * "from any device" headline and the sign-up offer. Since 1.11 the dialog opens only from poll routes (key
 * icon of the context bar, copy line of the answer page) - the anonymous banner points to the sign-up alone -,
 * so the hint mode is a fallback (nothing held on the poll, context not the route's) with its own title.
 */
export type GetPollAccessMode = 'loading' | 'link' | 'hint';

@Component({
    selector: 'get-poll-access',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatFormFieldModule,
        MatInputModule,
        MatCardModule,
        NgxEssentiaModule,
        ProviderSignInComponent,
        EmailSignupLoginComponent
    ],
    templateUrl: './get-poll-access.component.html',
    styleUrls: ['./get-poll-access.component.scss'],
})
export class GetPollAccessComponent implements OnInit {
    /** the join link - empty until the key is resolved, see ngOnInit */
    public pollUrlForField: string = '';
    public mode: GetPollAccessMode = 'loading';
    /** organizer link (edit the event) or response link (edit the own response) - picks the texts */
    public linkKind: 'poll' | 'vote' = 'poll';
    @Input() dialogRef: MatDialogRef<any>;

    constructor(
        private uiMessage: UIMesageService,
        private logger: LoggerService,
        public responsiveService: ResponsiveService,
        public headerService: HeaderService,
        private navigationService: NavigationService,
        public currentPollService: CurrentPollService,
        public trans: TranslationGeneralService,
        private clipboardService: ClipboardService,
        private googleAnayltics: GoogleAnalyticsService,
        private authService: AuthService,
        private userService: UserService,
        private routeContextService: RouteContextService,
    ) { }

    async ngOnInit(): Promise<void> {
        this.logger.log('ngOnInit: ');
        this.googleAnayltics.emitSimpleEvent('permanent_access_link')
        // the poll is the route's, not the context's: the context lingers after leaving a poll route
        // (workorder poll-scoped access 1.7)
        const pollRoute = this.routeContextService.pollRoute;
        if (!pollRoute || !this.currentPollService.isCurrentPoll(pollRoute.ownerId, pollRoute.pollId)) {
            // no poll route (My Events and other pages) or a context that is not the route's poll:
            // no link in phase 1, only the account hint
            this.mode = 'hint';
            return;
        }
        // the key resolution needs a settled sign-in; right after an anonymous sign-up the own user
        // record is the signal that the session is there (workorder-auth-window-option-access-link-2026-10-04)
        try {
            await this.userService.whenOwnDbUserLoaded(this.authService.uid);
        } catch (error) {
            this.logger.warn('GetPollAccessComponent: user record not loaded, no access link', error);
            this.uiMessage.warning(this.trans.translate('internalError'));
            this.mode = 'hint';
            return;
        }
        let parts: JoinLinkParts | null;
        try {
            parts = await this.navigationService.getJoinLinkParts();
        } catch (error) {
            this.logger.warn('GetPollAccessComponent: join link parts not resolved, no access link', error);
            // no connection (also after the one retry of the key creation): say so, the dialog can be opened again
            const connectionProblem = isConnectionLost(error) || isNetworkRequestFailed(error);
            this.uiMessage.warning(this.trans.translate(connectionProblem ? 'authError.networkRequestFailed' : 'internalError'));
            this.mode = 'hint';
            return;
        }
        if (!parts) {
            this.mode = 'hint';
            return;
        }
        this.linkKind = parts.kind;
        this.pollUrlForField = this.navigationService.composeJoinUrl(parts);
        this.mode = 'link';
        // never the url itself: it carries the key
        this.logger.log('GetPollAccessComponent: join link composed', parts.kind, parts.route);
        await this.copyToClipboard();
    }

    public get explanationKey(): string {
        return this.linkKind === 'vote' ? 'getPollAccess.dontShareVote' : 'getPollAccess.dontSharePoll';
    }

    /**
     * Card headline: the answer text for a vote link; for the organizer link "pass it on" for a signed-in user
     * (they come back by logging in) and "from any device" for an anonymous user (the link is their way back).
     */
    public get headlineKey(): string {
        if (this.linkKind === 'vote') return 'getPollAccess.dialogHeadline2Vote';
        return this.isRegistered ? 'getPollAccess.dialogHeadline2Registered' : 'getPollAccess.dialogHeadline2';
    }

    /** signed in with an account (not anonymous): no sign-up offer, pass-on headline (1.10) */
    public get isRegistered(): boolean {
        return this.authService.isLoggedIn && !this.authService.isAnonymous;
    }

    /** the "-- or -- sign up for free" offer is for anonymous users only (1.10) */
    public get showSignUp(): boolean {
        return !this.isRegistered;
    }

    /**
     * Title of the dialog shell (GetAccessDialogComponent reads it): "Organizer link" for a poll link,
     * "Response link" for a vote link; in hint mode (no link) "Your events on every device" - the
     * promise of the hint text (since 1.11 the hint mode is a fallback only: the anonymous banner no longer
     * opens the dialog, the entries are the key icon and the answer page, both on a poll route).
     */
    public get titleKey(): string {
        if (this.mode === 'hint') return 'getPollAccess.dialogHeadlineHint';
        return this.linkKind === 'vote' ? 'getPollAccess.dialogHeadlineVote' : 'getPollAccess.dialogHeadline';
    }

    /** the copy button names the link it copies (workorder 1.12) */
    public get copyTooltipKey(): string {
        return this.linkKind === 'vote' ? 'getPollAccess.copyLinkTooltipVote' : 'getPollAccess.copyLinkTooltipPoll';
    }

    /** closes the dialog whether or not the copy worked - the toast tells the difference */
    public handleClickCopyToClipboard() {
        this.copyToClipboard();
        this.dialogRef?.close()

    }

    /**
     * The automatic copy on open runs after the key resolution - outside the click gesture it usually
     * fails (always in Safari); then no toast, the link stays in the field (workorder 1.8).
     */
    public async copyToClipboard(): Promise<void> {
        if (!this.pollUrlForField) {
            // still resolving the key (ngOnInit) or hint mode - nothing to copy
            return;
        }
        if (await this.clipboardService.copy(this.pollUrlForField)) {
            this.uiMessage.success(this.trans.translate('poll.urlCopied'), null, 'bottom');
        }
    }

    public handleClickSignUp() {
        this.navigationService.navigate(this.trans.getRoutePath('upgradeAnonymous'))
        this.dialogRef?.close()

    }

}
